<script lang="ts" setup>
import { inject, onMounted, ref } from "vue";
import { vClickOutside } from "../../directives/v-click-outside";
import MolarMass from "./molar-mass.vue";
import UnitConverter from "./unit-converter.vue";
import PeriodicTable from "./periodic-table.vue";
import PpmToMgl from "./ppm-to-mgl.vue";
import CsvUpload from "../csv-upload.vue";
import ExcelUpload from "../excel-upload.vue";

import { OverlaysService } from "../../services/overlays.service";

const overlaysSvc = inject<OverlaysService>("overlaysSvc");

const { tools } = defineProps({
    tools: {
        type: Array<any>,
        required: true,
    },
    csvFlag: {
        type: String,
        required: false,
        default: "Nein",
    },
});

const currentTool = ref("molarmass");

const getToolByHandle = (handle: string) => {
    return tools.find((tool) => tool.toolHandle === handle);
};

onMounted(() => {
    // Any setup if needed
    // console.log("Tools Slideup mounted:", tools);
});
</script>

<template>
    <div
        class="w-full bottom-0 fixed left-0 z-50"
        v-click-outside="() => (overlaysSvc.currentTab = null)"
    >
        <div class="flex flex-row gap-4 justify-end px-4 sm:px-8 md:px-20">
            <div
                class="font-bold text-f5 py-2 px-4 rounded-t-s cursor-pointer shadow"
                :class="{
                    'bg-primary text-white': overlaysSvc.currentTab === 'tools',
                    'bg-white text-primary': overlaysSvc.currentTab !== 'tools',
                }"
                @click.stop="overlaysSvc.currentTab = 'tools'"
            >
                Tools
            </div>
            <div
                class="font-bold text-f5 py-2 px-4 rounded-t-s cursor-pointer shadow"
                :class="{
                    'bg-primary text-white': overlaysSvc.currentTab === 'CSV',
                    'bg-white text-primary': overlaysSvc.currentTab !== 'CSV',
                }"
                @click.stop="overlaysSvc.currentTab = 'CSV'"
                v-if="csvFlag === 'Ja'"
            >
                CSV
            </div>
            <div
                class="font-bold text-f5 py-2 px-4 rounded-t-s cursor-pointer shadow"
                :class="{
                    'bg-primary text-white': overlaysSvc.currentTab === 'XLSX',
                    'bg-white text-primary': overlaysSvc.currentTab !== 'XLSX',
                }"
                @click.stop="overlaysSvc.currentTab = 'XLSX'"
                v-if="csvFlag === 'Excel'"
            >
                XLSX
            </div>
        </div>
        <div
            class="bg-primary grid grid-cols-24 py-12"
            v-if="overlaysSvc.currentTab != null"
        >
            <div class="col-start-3 col-end-23 relative tools-white">
                <button
                    @click="overlaysSvc.currentTab = null"
                    class="absolute right-0 top-3 text-grey-500 hover:text-grey-700"
                >
                    <i class="fal fa-times text-f3 text-white"></i>
                </button>
                <div v-if="overlaysSvc.currentTab === 'tools'">
                    <h2 class="text-white">Tools</h2>
                    <div class="border-b border-white w-full">
                        <nav class="flex flex-row gap-11 mt-4">
                            <div
                                class="cursor-pointer pb-2 text-white text-f5"
                                :class="{
                                    'border-b-4 border-white font-bold':
                                        currentTool === 'molarmass',
                                }"
                                @click="currentTool = 'molarmass'"
                            >
                                Molar Mass
                            </div>
                            <div
                                class="cursor-pointer pb-2 text-white text-f5"
                                :class="{
                                    'border-b-4 border-white font-bold':
                                        currentTool === 'unitconverter',
                                }"
                                @click="currentTool = 'unitconverter'"
                            >
                                Unit Converter
                            </div>
                            <div
                                class="cursor-pointer pb-2 text-white text-f5"
                                :class="{
                                    'border-b-4 border-white font-bold':
                                        currentTool === 'periodictable',
                                }"
                                @click="currentTool = 'periodictable'"
                            >
                                Periodic Table
                            </div>
                            <div
                                class="cursor-pointer pb-2 text-white text-f5"
                                :class="{
                                    'border-b-4 border-white font-bold':
                                        currentTool === 'ppmtomgl',
                                }"
                                @click="currentTool = 'ppmtomgl'"
                            >
                                PPM to mg/L
                            </div>
                        </nav>
                    </div>
                    <div
                        class="grid grid-cols-2 justify-between gap-8 py-10"
                        v-if="currentTool == 'molarmass'"
                    >
                        <div class="max-w-[450px]">
                            <h3 class="text-white">
                                {{ getToolByHandle("molarmass").titel }}
                            </h3>
                            <div
                                class="redactor-content text-white"
                                v-html="
                                    getToolByHandle('molarmass').beschreibung
                                "
                            ></div>
                        </div>
                        <molar-mass></molar-mass>
                    </div>

                    <div
                        class="grid grid-cols-2 justify-between gap-8 py-10"
                        v-if="currentTool == 'unitconverter'"
                    >
                        <div class="max-w-[450px]">
                            <h3 class="text-white">
                                {{ getToolByHandle("unitconverter").titel }}
                            </h3>
                            <div
                                class="redactor-content text-white"
                                v-html="
                                    getToolByHandle('unitconverter')
                                        .beschreibung
                                "
                            ></div>
                        </div>
                        <unit-converter></unit-converter>
                    </div>

                    <div
                        class="grid grid-cols-2 justify-between gap-8 py-10"
                        v-if="currentTool == 'periodictable'"
                    >
                        <div class="max-w-[450px]">
                            <h3 class="text-white">
                                {{ getToolByHandle("periodictable").titel }}
                            </h3>
                            <div
                                class="redactor-content text-white"
                                v-html="
                                    getToolByHandle('periodictable')
                                        .beschreibung
                                "
                            ></div>
                        </div>
                        <periodic-table></periodic-table>
                    </div>

                    <div
                        class="grid grid-cols-2 justify-between gap-8 py-10"
                        v-if="currentTool == 'ppmtomgl'"
                    >
                        <div class="max-w-[450px]">
                            <h3 class="text-white">
                                {{ getToolByHandle("ppmtomgl").titel }}
                            </h3>
                            <div
                                class="redactor-content text-white"
                                v-html="
                                    getToolByHandle('ppmtomgl').beschreibung
                                "
                            ></div>
                        </div>
                        <ppm-to-mgl></ppm-to-mgl>
                    </div>
                </div>

                <div class="" v-if="overlaysSvc.currentTab === 'CSV'">
                    <csv-upload :csv-flag="csvFlag">
                        <slot name="csv"></slot>
                    </csv-upload>
                </div>

                <div class="" v-if="overlaysSvc.currentTab === 'XLSX'">
                    <h2 class="text-white mb-10">XLSX</h2>
                    <div class="text-white redactor-content">
                        <slot name="excel"></slot>
                    </div>
                    <excel-upload></excel-upload>
                </div>
            </div>
        </div>
    </div>
</template>
